import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface NavItem {
  label: string;
  to: string;
  children?: { label: string; to: string; desc: string }[];
}

const navItems: NavItem[] = [
  { label: "首页", to: "/" },
  {
    label: "动漫分类",
    to: "/category/最新",
    children: [
      { label: "最新动画", to: "/category/最新", desc: "近期上线的全新作品" },
      { label: "热门推荐", to: "/category/热门", desc: "人气最高的动画作品" },
      { label: "编辑推荐", to: "/category/推荐", desc: "精选编辑推荐佳作" },
      { label: "资讯作品", to: "/category/资讯", desc: "作品与资讯动态合集" },
    ],
  },
  { label: "资讯", to: "/news" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDrawerOpen(false);
    setDropdownOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isActive = useMemo(() => {
    return (to: string) => {
      if (to === "/") return location.pathname === "/";
      if (to === "/news") return location.pathname.startsWith("/news");
      return location.pathname.startsWith("/category");
    };
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setDrawerOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/90 backdrop-blur-md">
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[72px] md:px-8 xl:px-12">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
            <i className="ri-movie-2-fill" />
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">
            <span className="text-primary-500">58</span>动漫
          </span>
        </Link>

        <nav className="hidden flex-1 items-center gap-1 md:flex">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                ref={dropdownRef}
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? "text-primary-600"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute left-0 top-full z-50 w-64 animate-scale-in pt-2">
                    <div className="rounded-lg border border-background-200 bg-background-50 p-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="flex flex-col gap-0.5 rounded-md px-3 py-2 transition-colors hover:bg-primary-50"
                        >
                          <span className="text-sm font-medium text-foreground-900">{child.label}</span>
                          <span className="text-xs text-foreground-500">{child.desc}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.to) ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:flex">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 transition-colors focus-within:border-primary-300 focus-within:bg-background-50">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="搜索动漫 / 资讯"
                aria-label="站内搜索"
                className="w-28 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-40"
              />
            </div>
          </form>

          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="打开菜单"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-700 md:hidden"
          >
            <i className="ri-menu-2-line text-xl" />
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute right-0 top-0 flex h-full w-[78%] max-w-xs animate-fade-in flex-col gap-6 overflow-y-auto bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg font-extrabold text-foreground-950">
                <span className="text-primary-500">58</span>动漫
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭菜单"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={submitSearch}>
              <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  placeholder="搜索动漫 / 资讯"
                  aria-label="站内搜索"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
            </form>

            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <div key={item.label} className="flex flex-col">
                  <Link
                    to={item.to}
                    className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-primary-50"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="ml-3 flex flex-col border-l border-background-200 pl-3">
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="rounded-md px-3 py-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/search"
                className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-primary-50"
              >
                搜索
              </Link>
            </nav>

            <div className="mt-auto rounded-lg bg-primary-50 p-4">
              <p className="text-sm font-semibold text-primary-700">58动漫</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                最新动漫资讯、动画作品展示与行业动态，一站读懂二次元。
              </p>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}